/**
 * DATA MODULE - Read-only data visualization and export
 * For SUPER_ADMIN_SAAS only
 * 
 * Three views:
 * 1. CLINICAL - Operational patient-level data
 * 2. RESEARCH - Pseudonymized structured data
 * 3. BUSINESS - Aggregated metrics
 */

import { useState, useEffect } from "react";
import api from "@/api";
import { SectionTabs } from "@/components/shared/SectionTabs";
import { Database, FileText, FlaskConical, BarChart3, Filter, Activity, TrendingUp, X, Microscope, Target, ShieldCheck, BriefcaseBusiness } from "lucide-react";
import StudiesView from "./StudiesView";
import ClinicalIntelligenceView from "./ClinicalIntelligenceView";
import InteroperabilityView from "./InteroperabilityView";
import PharmaAnalyticsView from "./PharmaAnalyticsView";
import ClinicalView from "./data/ClinicalView";
import ResearchView from "./data/ResearchView";
import BusinessView from "./data/BusinessView";
import QualityGovernanceView from "./data/QualityGovernanceView";
import DataProjectsView from "./data/DataProjectsView";
import CohortBuilderView from "./data/CohortBuilderView";
import InsightsView from "./data/InsightsView";

// ========================
// MAIN DATA TAB COMPONENT
// ========================
export default function DataTab() {
  const [activeView, setActiveView] = useState("clinical");
  const [filters, setFilters] = useState({
    clinicId: "",
    startDate: "",
    endDate: "",
    cancerType: "",
    treatmentType: "",
    alertLevel: "",
    protocol: "",
  });
  const [filterOptions, setFilterOptions] = useState(null);
  const [loading, setLoading] = useState(false);

  // Fetch filter options on mount
  useEffect(() => {
    fetchFilterOptions();
  }, []);

  const fetchFilterOptions = async () => {
    try {
      const res = await api.get("/data/filters");
      setFilterOptions(res.data);
    } catch (err) {
      // error handled by toast
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(f => ({ ...f, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({
      clinicId: "",
      startDate: "",
      endDate: "",
      cancerType: "",
      treatmentType: "",
      alertLevel: "",
      protocol: "",
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
            <Database className="w-5 h-5 text-indigo-600" />
            Module DATA
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            Visualisation, filtrage et export des données HANA
          </p>
        </div>
      </div>

      {/* Global Filters Bar */}
      <div className="dash-card bg-slate-50">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="w-4 h-4 text-slate-500" />
          <span className="text-sm font-medium text-slate-700">Filtres globaux</span>
          {Object.values(filters).some(v => v) && (
            <button
              onClick={clearFilters}
              data-testid="data-filters-reset-button"
              className="ml-auto flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700"
            >
              <X className="w-3 h-3" />
              Réinitialiser
            </button>
          )}
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {/* Clinic Filter */}
          <div>
            <label className="block text-xs text-slate-500 mb-1">Clinique</label>
            <select
              value={filters.clinicId}
              onChange={e => handleFilterChange("clinicId", e.target.value)}
              data-testid="data-filter-clinic-select"
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white"
            >
              <option value="">Toutes</option>
              {filterOptions?.clinics?.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>

          {/* Date Start */}
          <div>
            <label className="block text-xs text-slate-500 mb-1">Date début</label>
            <input
              type="date"
              value={filters.startDate}
              onChange={e => handleFilterChange("startDate", e.target.value)}
              data-testid="data-filter-start-date-input"
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
            />
          </div>

          {/* Date End */}
          <div>
            <label className="block text-xs text-slate-500 mb-1">Date fin</label>
            <input
              type="date"
              value={filters.endDate}
              onChange={e => handleFilterChange("endDate", e.target.value)}
              data-testid="data-filter-end-date-input"
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
            />
          </div>

          {/* Cancer Type */}
          <div>
            <label className="block text-xs text-slate-500 mb-1">Type de cancer</label>
            <select
              value={filters.cancerType}
              onChange={e => handleFilterChange("cancerType", e.target.value)}
              data-testid="data-filter-cancer-type-select"
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white"
            >
              <option value="">Tous</option>
              {filterOptions?.cancer_types?.map(t => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>

          {/* Treatment Type */}
          <div>
            <label className="block text-xs text-slate-500 mb-1">Traitement</label>
            <select
              value={filters.treatmentType}
              onChange={e => handleFilterChange("treatmentType", e.target.value)}
              data-testid="data-filter-treatment-type-select"
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white"
            >
              <option value="">Tous</option>
              {filterOptions?.treatment_types?.map(t => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>

          {/* Alert Level */}
          <div>
            <label className="block text-xs text-slate-500 mb-1">Niveau alerte</label>
            <select
              value={filters.alertLevel}
              onChange={e => handleFilterChange("alertLevel", e.target.value)}
              data-testid="data-filter-alert-level-select"
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white"
            >
              <option value="">Tous</option>
              {filterOptions?.alert_levels?.map(l => (
                <option key={l} value={l}>{l}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Sub-tabs */}
      <SectionTabs
        variant="secondary"
        testIdPrefix="data-tab"
        active={activeView}
        onChange={setActiveView}
        tabs={[
          { id: "clinical", label: "Vue Clinique", icon: FileText },
          { id: "research", label: "Vue Recherche", icon: FlaskConical },
          { id: "business", label: "Vue Business", icon: BarChart3 },
          { id: "quality", label: "Qualité RWE", icon: ShieldCheck },
          { id: "projects", label: "Projets Data", icon: BriefcaseBusiness },
          { id: "cohort", label: "Cohort Builder", icon: Target },
          { id: "insights", label: "Insights", icon: TrendingUp },
          { id: "studies", label: "Etudes", icon: Microscope },
          { id: "intelligence", label: "Intelligence Clinique", icon: Activity },
          { id: "interop", label: "Interoperabilite", icon: Database },
          { id: "pharma", label: "Pharma Analytics", icon: TrendingUp },
        ]}
      />

      {/* Views */}
      {activeView === "clinical" && <ClinicalView filters={filters} />}
      {activeView === "research" && <ResearchView filters={filters} />}
      {activeView === "business" && <BusinessView filters={filters} />}
      {activeView === "quality" && <QualityGovernanceView filters={filters} />}
      {activeView === "projects" && <DataProjectsView filters={filters} />}
      {activeView === "cohort" && <CohortBuilderView />}
      {activeView === "insights" && <InsightsView />}
      {activeView === "studies" && <StudiesView />}
      {activeView === "intelligence" && <ClinicalIntelligenceView />}
      {activeView === "interop" && <InteroperabilityView />}
      {activeView === "pharma" && <PharmaAnalyticsView />}
    </div>
  );
}
